import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGql, Card, CardContent, Skeleton } from '@ah/design';
import { BottomSheet } from '@ah/design/mobile';
import {
  ArrowLeft, ArrowRight, BarChart3, Quote, Settings, Sparkles, X,
} from 'lucide-react';
import {
  BOOK_READER_QUERY,
  type Block,
  type BookReaderData,
  useMarkChapterRead,
} from '../shared/index.ts';

type ThemeKey = 'auto' | 'light' | 'sepia' | 'dark';
type SizeKey = 'S' | 'M' | 'L' | 'XL';

interface Prefs {
  size?: SizeKey;
  theme?: ThemeKey;
}

const SIZE_PX: Record<SizeKey, number> = { S: 16, M: 18, L: 20, XL: 22 };
const THEMES: ThemeKey[] = ['auto', 'light', 'sepia', 'dark'];
const SIZES: SizeKey[] = ['S', 'M', 'L', 'XL'];

const PREFS_KEY = 'books.reader.prefs';

function useReaderPrefs() {
  const [prefs, setPrefs] = useState<Prefs>(() => {
    if (typeof window === 'undefined') return {};
    try {
      return JSON.parse(window.localStorage.getItem(PREFS_KEY) ?? '{}') as Prefs;
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
  }, [prefs]);
  return [prefs, setPrefs] as const;
}

export function BookReader() {
  const gql = useGql();
  const navigate = useNavigate();
  const { olid = '', chapter: chapterStr = '1' } = useParams();
  const olKey = `/works/${olid}`;
  const chapterIdx = Math.max(1, parseInt(chapterStr, 10) || 1) - 1;

  const { data, isLoading, error } = useQuery({
    queryKey: ['books', 'reader', olKey],
    queryFn: async () => {
      const res = await gql.request<{ book: BookReaderData | null }>(BOOK_READER_QUERY, { olKey });
      return res.book;
    },
    enabled: !!olid,
  });

  const [prefs, setPrefs] = useReaderPrefs();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const size: SizeKey = prefs.size ?? 'M';
  const theme: ThemeKey = prefs.theme ?? 'auto';
  const sizePx = SIZE_PX[size];
  const themeClass = theme === 'auto' ? '' : `reader-theme-${theme}`;

  const scrollRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [chapterIdx]);

  const markChapterRead = useMarkChapterRead();
  const markTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!data?.summary?.chapters?.length) return;
    const n = chapterIdx + 1;
    if (markTimer.current) window.clearTimeout(markTimer.current);
    markTimer.current = window.setTimeout(() => markChapterRead(olid, n), 4000);
    return () => {
      if (markTimer.current) window.clearTimeout(markTimer.current);
    };
  }, [olid, chapterIdx, data?.summary?.chapters?.length, markChapterRead]);

  const closeToDetail = () => navigate(`/library/books/${olid}`, { replace: true });

  if (isLoading) {
    return (
      <div
        className={`fixed inset-0 flex flex-col bg-background ${themeClass}`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <ReaderTopBar title="" onClose={closeToDetail} onSettings={() => setSettingsOpen(true)} />
        <main className="flex-1 overflow-y-auto">
          <article className="mx-auto max-w-[640px] px-5 py-6 space-y-4">
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-4 w-1/3" />
            <div className="space-y-2.5 pt-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[92%]" />
              <Skeleton className="h-4 w-[98%]" />
              <Skeleton className="h-4 w-[70%]" />
            </div>
            <div className="space-y-2.5 pt-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[95%]" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[60%]" />
            </div>
          </article>
        </main>
      </div>
    );
  }

  if (error || !data || !data.summary || !data.summary.chapters.length) {
    return (
      <div
        className={`fixed inset-0 flex flex-col bg-background ${themeClass}`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <ReaderTopBar title="" onClose={closeToDetail} onSettings={() => setSettingsOpen(true)} />
        <main className="flex-1 grid place-items-center px-6 text-center">
          <p className="text-sm text-destructive">Summary not available.</p>
        </main>
      </div>
    );
  }

  const chapters = data.summary.chapters;
  const total = chapters.length;
  const safeIdx = Math.min(chapterIdx, total - 1);
  const current = chapters[safeIdx];
  const isFirst = safeIdx === 0;
  const isLast = safeIdx === total - 1;

  const go = (n: number) => navigate(`/library/books/${olid}/read/${n}`, { replace: true });

  return (
    <div
      className={`fixed inset-0 flex flex-col bg-background ${themeClass}`}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <ReaderTopBar
        title={current.title}
        onClose={closeToDetail}
        onSettings={() => setSettingsOpen(true)}
      />
      <main ref={scrollRef as React.RefObject<HTMLElement>} className="flex-1 overflow-y-auto">
        <article
          className="mx-auto max-w-[640px] px-5 pt-6 pb-24"
          style={{ fontSize: sizePx, lineHeight: 1.6 }}
        >
          <p className="text-[11px] uppercase tracking-wide opacity-60 mb-2">
            Chapter {safeIdx + 1} of {total}
          </p>
          <h1 className="text-[1.6em] font-bold leading-tight mb-6">{current.title}</h1>
          <div className="space-y-5">
            {current.blocks.map((b, i) => (
              <BlockView key={i} block={b} />
            ))}
          </div>
          {current.keyTakeaway ? (
            <Card className="mt-8 bg-primary/5 border-primary/20">
              <CardContent className="py-4 flex gap-3 items-start">
                <Sparkles className="text-primary flex-shrink-0 mt-0.5" size={16} />
                <div>
                  <p className="text-[11px] uppercase tracking-wide opacity-60 mb-1">
                    Key takeaway
                  </p>
                  <p className="text-base font-semibold leading-snug">{current.keyTakeaway}</p>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </article>
      </main>

      <ReaderChapterNav
        prevDisabled={isFirst}
        nextDisabled={isLast}
        onPrev={() => go(safeIdx)}
        onNext={() => go(safeIdx + 2)}
      />

      <BottomSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Reader settings"
        footer={
          <button
            type="button"
            onClick={() => setSettingsOpen(false)}
            className="w-full flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm active:scale-[0.98] transition-all"
          >
            Done
          </button>
        }
      >
        <div className="px-4 py-4 space-y-6">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Font size</p>
            <div className="flex gap-2">
              {SIZES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setPrefs((p) => ({ ...p, size: s }))}
                  className={
                    'flex-1 py-2 rounded-lg border text-sm font-medium ' +
                    (size === s
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border text-muted-foreground')
                  }
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Theme</p>
            <div className="grid grid-cols-4 gap-2">
              {THEMES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setPrefs((p) => ({ ...p, theme: t }))}
                  className={
                    'py-2 rounded-lg border text-xs font-medium capitalize ' +
                    (theme === t
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border text-muted-foreground')
                  }
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

function ReaderTopBar({
  title,
  onClose,
  onSettings,
}: {
  title: string;
  onClose: () => void;
  onSettings: () => void;
}) {
  return (
    <header className="relative flex items-center h-12 px-2 border-b border-border/50">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close reader"
        className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-accent"
      >
        <X size={18} />
      </button>
      <span className="absolute inset-x-12 text-center text-sm font-medium truncate pointer-events-none">
        {title}
      </span>
      <button
        type="button"
        onClick={onSettings}
        aria-label="Reader settings"
        className="ml-auto flex items-center justify-center w-9 h-9 rounded-lg hover:bg-accent"
      >
        <Settings size={18} />
      </button>
    </header>
  );
}

function ReaderChapterNav({
  prevDisabled,
  nextDisabled,
  onPrev,
  onNext,
}: {
  prevDisabled: boolean;
  nextDisabled: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="border-t border-border/50 px-3 py-2 flex items-center gap-2">
      <button
        type="button"
        disabled={prevDisabled}
        onClick={onPrev}
        className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-border bg-card text-sm font-medium disabled:opacity-40"
      >
        <ArrowLeft size={14} /> Previous
      </button>
      <button
        type="button"
        disabled={nextDisabled}
        onClick={onNext}
        className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium disabled:opacity-40"
      >
        Next <ArrowRight size={14} />
      </button>
    </div>
  );
}

function BlockView({ block }: { block: Block }) {
  if (block.kind === 'quote') {
    return (
      <div className="border-l-4 border-primary/40 pl-4 py-1 my-2">
        <Quote size={12} className="opacity-50 mb-1" />
        <p className="italic leading-snug font-semibold">"{block.body}"</p>
        {block.source ? (
          <p className="text-xs opacity-60 mt-1.5">— {block.source}</p>
        ) : null}
      </div>
    );
  }
  if (block.kind === 'stat') {
    return (
      <Card className="bg-accent/40 border-none my-2">
        <CardContent className="py-3 flex gap-3 items-start">
          <BarChart3 className="opacity-60 flex-shrink-0 mt-0.5" size={14} />
          <div>
            <p className="leading-snug">{block.body}</p>
            {block.source ? (
              <p className="text-xs opacity-60 mt-1">— {block.source}</p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    );
  }
  return <p>{block.body}</p>;
}
